import * as T from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Flex, Image } from '@olist/united';

import { StepContent } from '../../../components/index';
import { AnimatedImage, Skeleton } from '../../../components/layout';
import {
  AnimatedBankAccountStep,
  AnimatedLogisticAddressStep,
  AnimatedSubscriptionStep,
  MoneyFlag,
} from '../../../images/index';
import {
  ONBOARDING_PLAN_PRESENTATION_URL,
  PERSONAL_INFORMATION_URL,
  SETTINGS_BANK_ACCOUNT_URL,
  SUBSCRIPTION_PAYMENT_URL,
} from '../../../utils/urls';
import { stepNames } from '../constants';

const imageParams = {
  style: { maxWidth: '448px' },
};

export const LogisticAddressStepContent = () => {
  const { t } = useTranslation();

  return (
    <StepContent
      id={stepNames.LOGISTIC_ADDRESS}
      title={t`inform your logistic address`}
      description={t`We need to know where orders will be sent from for freight calculation and for after-sales procedures, such as product returns.`}
      callToAction={t`register zip code`}
      image={
        <Box minHeight={[0, 0, '258px']}>
          <AnimatedImage src={AnimatedLogisticAddressStep} {...imageParams} />
        </Box>
      }
      href={PERSONAL_INFORMATION_URL}
    />
  );
};

export const BankAccountStepContent = () => {
  const { t } = useTranslation();

  return (
    <StepContent
      id={stepNames.BANK_ACCOUNT}
      title={t`get paid for your sales`}
      description={t`We take care of all the financial procedures and deposit the values in your company bank account associated with the registered document.`}
      callToAction={t`inform your bank details`}
      image={
        <Box minHeight={[0, 0, '258px']}>
          <AnimatedImage src={AnimatedBankAccountStep} {...imageParams} />
        </Box>
      }
      href={SETTINGS_BANK_ACCOUNT_URL}
    />
  );
};

export const OutboundSubscriptionWithNoPlanSelected = () => {
  const { t } = useTranslation();

  return (
    <StepContent
      id={stepNames.SUBSCRIPTION}
      title={t`Contact your consultor to pay your plan.`}
      callToAction={t`choose my plan`}
      image={
        <Box minHeight={[0]}>
          <Image src={MoneyFlag} {...imageParams} />
        </Box>
      }
      infoTextProps={{ mt: 0, pl: 0 }}
    />
  );
};

export const SubscriptionStepContent = ({ isFromInvite }) => {
  const { t } = useTranslation();

  return (
    <StepContent
      id={stepNames.SUBSCRIPTION}
      title={t`choose the ideal subscription plan`}
      description={
        <Trans i18nKey="planBusiness">
          <b>olist store</b> has the right plan for every moment of your business. Choose yours and
          start selling.
        </Trans>
      }
      callToAction={t`choose my plan`}
      image={
        <Box minHeight={[0, 0, '276px']}>
          <AnimatedImage src={AnimatedSubscriptionStep} {...imageParams} />
        </Box>
      }
      href={isFromInvite ? SUBSCRIPTION_PAYMENT_URL : ONBOARDING_PLAN_PRESENTATION_URL}
    />
  );
};

SubscriptionStepContent.propTypes = {
  isFromInvite: T.bool,
};

SubscriptionStepContent.defaultProps = {
  isFromInvite: false,
};

export const SkeletonStepContent = () => (
  <Flex flexDirection="column" data-testid="skeleton-content">
    <Skeleton width="448px" height="257px" />
    <Flex flexDirection="column" p={3} mt={6} width="448px">
      <Skeleton width="100%" height="29px" />
      <Skeleton width="100%" height="23px" mt={3} />
      <Skeleton width="100%" height="23px" mt={1} />
      <Skeleton width="100%" height="23px" mt={1} />
      <Skeleton width="200px" height="46px" mt={4} />
    </Flex>
  </Flex>
);
